import React, {useMemo} from 'react';
import {Box, ButtonBase, Chip, InputBase, MenuItem, Select, Skeleton, Stack, Tooltip, Typography} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {useNavigate} from 'react-router-dom';
import {HugeiconsIcon} from '@hugeicons/react';
import {Add01Icon, DatabaseIcon, LockIcon, LockOpenIcon, Search01Icon} from '@hugeicons/core-free-icons';
import {SubProjectOption} from '../../SubProjectSelect';
import {STAT_TONES} from '../../dashboard/components/StatTile';
import {
  flowUidAtom, mcpLoadingAtom, projectAtom, searchQueryAtom, selectedServerAtom, serversAtom, statusFilterAtom, subProjectAtom
} from '../atoms';

const TONES = Object.keys(STAT_TONES);
const CARD_SHADOW = "0 2px 12px rgba(35, 38, 77, 0.07)";
const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const controlSx = {
  height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.75, borderRadius: "12px", bgcolor: "#fff",
  boxShadow: CARD_SHADOW, color: "#8A8AA0", fontSize: 13,
};

const StatusDot = ({active}) => (
  <Box component={"span"} sx={{
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: active ? "#22C55E" : "#C9CAD8",
    ...(active && {
      animation: "mcp-pulse 2.2s infinite",
      "@keyframes mcp-pulse": {
        "0%": {boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.5)"},
        "70%": {boxShadow: "0 0 0 7px rgba(34, 197, 94, 0)"},
        "100%": {boxShadow: "0 0 0 0 rgba(34, 197, 94, 0)"},
      },
    }),
  }}/>
);

const ServerCard = ({server, index, onOpen}) => {
  const tone = STAT_TONES[TONES[index % TONES.length]];
  const tools = server.tools || [];
  const isSecure = server.auth_token === true;

  return (
    <ButtonBase
      onClick={() => onOpen(server)}
      sx={{
        display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.75, textAlign: "left", p: 2.25, borderRadius: "18px",
        bgcolor: "#fff", boxShadow: CARD_SHADOW, minWidth: 0,
        transition: `transform 0.45s ${SPRING}, box-shadow 0.25s ease`,
        "&:hover": {transform: "translateY(-4px)", boxShadow: "0 10px 28px rgba(35, 38, 77, 0.12)"},
      }}
    >
      <Stack direction={"row"} alignItems={"center"} gap={1.5}>
        <Box sx={{
          width: 44, height: 44, borderRadius: "14px", flexShrink: 0, display: "flex", alignItems: "center",
          justifyContent: "center", color: tone.color, bgcolor: tone.bg,
        }}>
          <HugeiconsIcon icon={DatabaseIcon} size={21}/>
        </Box>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography noWrap sx={{fontSize: 16, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>{server.name}</Typography>
          <Typography noWrap sx={{fontFamily: "monospace", fontSize: 12, color: "#8A8AA0"}}>{server.endpoint}</Typography>
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={0.75} sx={{flexShrink: 0}}>
          <StatusDot active={server.active}/>
          <Typography sx={{fontSize: 12, fontWeight: 600, color: "#6B6B80"}}>{server.active ? "Live" : "Off"}</Typography>
        </Stack>
      </Stack>

      <Stack direction={"row"} gap={0.75} flexWrap={"wrap"} sx={{minHeight: 26}}>
        {tools.slice(0, 3).map(tool => (
          <Box key={tool.id || tool.name} component={"span"} sx={{
            px: 1.1, py: 0.25, borderRadius: "8px", fontFamily: "monospace", fontSize: 11.5, color: "#6B6B80", bgcolor: "#FAFAFD",
            maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {tool.name}
          </Box>
        ))}
        {tools.length > 3 && (
          <Box component={"span"} sx={{px: 1.1, py: 0.25, borderRadius: "8px", fontSize: 12, color: "#6B6B80", bgcolor: "#FAFAFD"}}>
            +{tools.length - 3}
          </Box>
        )}
      </Stack>

      <Stack direction={"row"} alignItems={"center"} gap={2} sx={{mt: "auto", fontSize: 12, color: "#8A8AA0"}}>
        <span><b style={{color: "#23264D"}}>{tools.length}</b> tool{tools.length === 1 ? "" : "s"}</span>
        <Tooltip title={isSecure ? "Connection is secure" : "Connection may not be secure"}>
          <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{ml: "auto", color: isSecure ? "#2F9A6A" : "#D9A13A", fontWeight: 600}}>
            <HugeiconsIcon icon={isSecure ? LockIcon : LockOpenIcon} size={13}/>
            {isSecure ? "Secure" : "Insecure"}
          </Stack>
        </Tooltip>
      </Stack>
    </ButtonBase>
  );
};

// Gallery of the project's MCP servers; opening one shows its detail page (selectedServerAtom)
export const MCPServerGallery = ({onAddClick}) => {
  const [servers] = useAtom(serversAtom);
  const [, setSelectedServer] = useAtom(selectedServerAtom);
  const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom);
  const [statusFilter, setStatusFilter] = useAtom(statusFilterAtom);
  const [loading] = useAtom(mcpLoadingAtom);
  const flowUid = useAtomValue(flowUidAtom);
  const project = useAtomValue(projectAtom);
  const subProject = useAtomValue(subProjectAtom);
  const navigate = useNavigate();
  const subProjects = project?.subProjects || [];

  const scopedServers = useMemo(() =>
    flowUid ? servers.filter(server => server.flow_uid === flowUid) : servers,
    [servers, flowUid]);

  const filteredServers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return scopedServers.filter(server => {
      const matchesSearch = server.name?.toLowerCase().includes(query) || server.endpoint?.toLowerCase().includes(query)
        || (server.tools || []).some(tool => tool.name?.toLowerCase().includes(query));
      const matchesStatus = statusFilter === 'All' || (statusFilter === 'Active' && server.active) || (statusFilter === 'Inactive' && !server.active);
      return matchesSearch && matchesStatus;
    });
  }, [scopedServers, searchQuery, statusFilter]);

  const handleChangeSubProject = (id) => {
    if (!project?.id) return;
    navigate(`/registry-project/${project.id}/${id}/mcp-registry`);
  };

  return (
    <Stack gap={2.5}>
      <Stack direction={"row"} gap={1.25} alignItems={"center"} flexWrap={"wrap"}>
        <Box component={"label"} sx={{...controlSx, width: {xs: "100%", sm: 300}, maxWidth: "100%"}}>
          <HugeiconsIcon icon={Search01Icon} size={16}/>
          <InputBase
            placeholder={"Search servers or tools"}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            inputProps={{"aria-label": "Search servers"}}
            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        {['All', 'Active', 'Inactive'].map(status => (
          <Chip
            key={status}
            label={status}
            onClick={() => setStatusFilter(status)}
            sx={{
              height: 32, fontWeight: 600, fontSize: 13, border: 'none', borderRadius: '999px',
              boxShadow: statusFilter === status ? 'none' : CARD_SHADOW,
              bgcolor: statusFilter === status ? 'rgba(132, 145, 247, 0.14)' : '#fff',
              color: statusFilter === status ? '#4B55C9' : '#6B6B80',
              '&:hover': {bgcolor: statusFilter === status ? 'rgba(132, 145, 247, 0.2)' : '#FAFAFD'},
            }}
          />
        ))}
        {subProjects.length > 0 && subProject && (
          <Select
            variant="standard"
            disableUnderline
            value={String(subProject.id)}
            onChange={(e) => handleChangeSubProject(e.target.value)}
            renderValue={() => <SubProjectOption subProject={subProject}/>}
            inputProps={{"aria-label": "Sub project"}}
            sx={{
              ml: "auto", maxWidth: 190, borderRadius: "999px", px: 1.25, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)",
              "& .MuiSelect-select": {py: 0.75, pr: "22px !important", display: "flex", alignItems: "center"},
              "& .MuiSelect-icon": {color: "#4B55C9"},
            }}
            MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
          >
            {subProjects.map(item => (
              <MenuItem key={item.id} value={String(item.id)} dense>
                <SubProjectOption subProject={item}/>
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>

      {loading && scopedServers.length === 0 ? (
        <Box sx={{display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))"}}>
          {Array.from({length: 6}).map((_, index) => (
            <Skeleton key={index} variant={"rounded"} height={170} sx={{borderRadius: "18px"}}/>
          ))}
        </Box>
      ) : (
        <Box sx={{display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))"}}>
          {filteredServers.map((server, index) => (
            <ServerCard key={server.id} server={server} index={index} onOpen={setSelectedServer}/>
          ))}
          <ButtonBase
            onClick={onAddClick}
            sx={{
              display: "flex", flexDirection: {xs: "row", sm: "column"}, gap: {xs: 1, sm: 1.25}, minHeight: {xs: 46, sm: 170},
              borderRadius: {xs: "12px", sm: "18px"}, color: {xs: "#4B55C9", sm: "#8A8AA0"}, fontSize: 14, fontWeight: 600,
              bgcolor: {xs: "rgba(132, 145, 247, 0.14)", sm: "rgba(132, 145, 247, 0.06)"},
              transition: "background-color .2s ease, color .2s ease",
              "&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)", color: "#4B55C9"},
            }}
          >
            <Box sx={{
              width: {xs: "auto", sm: 44}, height: {xs: "auto", sm: 44}, borderRadius: "50%", bgcolor: {xs: "transparent", sm: "#fff"},
              boxShadow: {xs: "none", sm: CARD_SHADOW}, color: "#8491F7", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <HugeiconsIcon icon={Add01Icon} size={20}/>
            </Box>
            Add MCP server
          </ButtonBase>
        </Box>
      )}

      {!loading && scopedServers.length > 0 && filteredServers.length === 0 && (
        <Typography sx={{fontSize: 13, color: "#8A8AA0", textAlign: "center"}}>No servers match your search or filter</Typography>
      )}
    </Stack>
  );
};
